/**
 * Customer Analytics Page
 * Client lifecycle, service, support, and domain analytics
 */

import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';

type ReportType = 'clients' | 'services' | 'support' | 'domains';

export const CustomerAnalyticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ReportType>('clients');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState<any>(null);

  // Date range state
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    loadReport();
  }, [activeTab, dateRange]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate).toISOString(),
      });

      const response = await adminApi.get(`/customer-analytics/${activeTab}?${params.toString()}`);
      setReportData(response.data.report);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
      console.error('Error loading report:', err);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'clients', label: 'Client Report', icon: '👥' },
    { id: 'services', label: 'Service Report', icon: '🖥️' },
    { id: 'support', label: 'Support Report', icon: '🎫' },
    { id: 'domains', label: 'Domain Report', icon: '🌐' },
  ];

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white">Customer Analytics</h1>
          <p className="text-slate-400 mt-1">Customer lifecycle and operational intelligence</p>
        </div>

        {/* Date Range Controls */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6 border border-slate-700/50">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-slate-300">From:</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
                }
                className="px-3 py-1.5 bg-slate-700/50 border border-gray-600 rounded text-white text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm text-slate-300">To:</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                className="px-3 py-1.5 bg-slate-700/50 border border-gray-600 rounded text-white text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 mb-6">
          <div className="flex border-b border-slate-700/50">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as ReportType)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-slate-700/50 text-white border-b-2 border-orange-500'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50/50'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Report Content */}
          <div className="p-6">
            {error && (
              <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : reportData ? (
              <>
                {activeTab === 'clients' && <ClientReportView data={reportData} />}
                {activeTab === 'services' && <ServiceReportView data={reportData} />}
                {activeTab === 'support' && <SupportReportView data={reportData} />}
                {activeTab === 'domains' && <DomainReportView data={reportData} />}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

// Client Report View Component
const ClientReportView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-6">
    {/* Summary Cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-orange-500/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Total Clients</p>
        <p className="text-2xl font-bold text-white mt-2">{data.summary.totalClients}</p>
      </div>

      <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-500/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">New Signups</p>
        <p className="text-2xl font-bold text-white mt-2">{data.summary.newSignups}</p>
      </div>

      <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border border-purple-500/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Active Clients</p>
        <p className="text-2xl font-bold text-white mt-2">{data.summary.activeClients}</p>
      </div>

      <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 border border-yellow-500/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Avg Lifetime Value</p>
        <p className="text-2xl font-bold text-white mt-2">
          ${data.summary.averageLifetimeValue.toFixed(2)}
        </p>
      </div>
    </div>

    {/* Top Clients */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Top Clients by Spending</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700/50/50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">
                Client
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">
                Email
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">
                Total Spent
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">
                Active Services
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">
                Member Since
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {data.topClients.map((client: any) => (
              <tr key={client.clientId} className="hover:bg-slate-700/50/30">
                <td className="px-4 py-2 text-sm text-white">{client.clientName}</td>
                <td className="px-4 py-2 text-sm text-slate-300">{client.email}</td>
                <td className="px-4 py-2 text-sm text-right text-green-400 font-medium">
                  ${client.totalSpent.toFixed(2)}
                </td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">
                  {client.activeServices}
                </td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">
                  {new Date(client.memberSince).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Status Distribution */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Client Status Distribution</h3>
      <div className="grid grid-cols-2 gap-4">
        {data.byStatus.map((status: any) => (
          <div key={status.status} className="bg-slate-700/50/50 rounded-lg p-4">
            <p className="text-sm text-slate-400">{status.status}</p>
            <p className="text-2xl font-bold text-white mt-1">{status.count}</p>
            <p className="text-xs text-slate-400 mt-1">
              {status.percentageOfTotal.toFixed(1)}% of total
            </p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Service Report View Component
const ServiceReportView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-6">
    {/* Summary Grid */}
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Total Services</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.totalServices}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Active</p>
        <p className="text-xl font-bold text-green-400 mt-1">{data.summary.activeServices}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Suspended</p>
        <p className="text-xl font-bold text-yellow-400 mt-1">{data.summary.suspendedServices}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Cancelled</p>
        <p className="text-xl font-bold text-red-400 mt-1">{data.summary.cancelledServices}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Churn Rate</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.churnRate}%</p>
      </div>
    </div>

    {/* Status Breakdown */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Status Breakdown</h3>
      <div className="space-y-3">
        {data.statusBreakdown.map((status: any) => (
          <div key={status.status} className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-24">
                <span className="text-sm text-slate-300 capitalize">{status.status}</span>
              </div>
              <div className="flex-1">
                <div className="bg-slate-700/50 rounded-full h-2 w-48">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${status.percentageOfTotal}%` }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-white font-medium">{status.count}</span>
              <span className="text-sm text-slate-400 w-16 text-right">
                {status.percentageOfTotal.toFixed(1)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* By Product Type */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Services by Product Type</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700/50/50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">
                Type
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">
                Total
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">
                Active
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">
                % of Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {data.byProductType.map((type: any) => (
              <tr key={type.productType} className="hover:bg-slate-700/50/30">
                <td className="px-4 py-2 text-sm text-white capitalize">{type.productType}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{type.count}</td>
                <td className="px-4 py-2 text-sm text-right text-green-400">{type.activeCount}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">
                  {type.percentageOfTotal.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Support Report View Component
const SupportReportView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-6">
    {/* Summary */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Total Tickets</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.totalTickets}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Open Tickets</p>
        <p className="text-xl font-bold text-yellow-400 mt-1">{data.summary.openTickets}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Closed Tickets</p>
        <p className="text-xl font-bold text-green-400 mt-1">{data.summary.closedTickets}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Resolution Rate</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.resolutionRate.toFixed(1)}%</p>
      </div>
    </div>

    {/* By Priority */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Tickets by Priority</h3>
      <div className="grid grid-cols-3 gap-4">
        {data.byPriority.map((priority: any) => (
          <div key={priority.priority} className="bg-slate-700/50/50 rounded-lg p-4">
            <p className="text-sm text-slate-400 capitalize">{priority.priority}</p>
            <p className="text-2xl font-bold text-white mt-1">{priority.count}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Common Issues */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Top 10 Common Issues</h3>
      <div className="space-y-2">
        {data.commonIssues.map((issue: any, index: number) => (
          <div key={index} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
            <span className="text-sm text-slate-300 truncate flex-1">{issue.subject}</span>
            <span className="text-sm font-medium text-white ml-4">{issue.count}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Domain Report View Component
const DomainReportView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-6">
    {/* Summary */}
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Total Domains</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.totalDomains}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Active Domains</p>
        <p className="text-xl font-bold text-green-400 mt-1">{data.summary.activeDomains}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Expiring Soon</p>
        <p className="text-xl font-bold text-red-400 mt-1">{data.summary.expiringDomains}</p>
        <p className="text-xs text-slate-400 mt-1">Within 30 days</p>
      </div>
    </div>

    {/* Recent Activity */}
    <div className="grid grid-cols-3 gap-4">
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Registrations</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.recentRegistrations}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Renewals</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.recentRenewals}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Transfers</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.recentTransfers}</p>
      </div>
    </div>

    {/* Top TLDs */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Top 10 TLDs</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700/50/50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">
                TLD
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">
                Count
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">
                % of Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {data.byTLD.map((tld: any) => (
              <tr key={tld.tld} className="hover:bg-slate-700/50/30">
                <td className="px-4 py-2 text-sm text-white">.{tld.tld}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{tld.count}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">
                  {tld.percentageOfTotal.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Expiring Domains */}
    {data.expiringList.length > 0 && (
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Domains Expiring Soon</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">
                  Domain
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">
                  Client
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">
                  Expiry Date
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">
                  Days Left
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {data.expiringList.map((domain: any) => (
                <tr key={domain.domainId} className="hover:bg-slate-700/50/30">
                  <td className="px-4 py-2 text-sm text-white font-mono">{domain.domainName}</td>
                  <td className="px-4 py-2 text-sm text-slate-300">{domain.clientName}</td>
                  <td className="px-4 py-2 text-sm text-right text-slate-300">
                    {new Date(domain.expiryDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-sm text-right">
                    <span
                      className={`font-medium ${
                        domain.daysUntilExpiry < 7
                          ? 'text-red-400'
                          : domain.daysUntilExpiry < 14
                          ? 'text-yellow-400'
                          : 'text-slate-300'
                      }`}
                    >
                      {domain.daysUntilExpiry} days
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

export default CustomerAnalyticsPage;
